import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, ApiError } from '@/api/client'
import type { JobDetail, JobWriteAudit } from '@/api/types'

/**
 * Job maintenance — the first screen that writes back into Sage.
 *
 * Everything here is Sage's data. Only the per diem location is editable,
 * because it is the one field the portal owns: the portal is where a wrong one
 * shows up, so the portal is where it should be fixable.
 *
 * The rest is shown read-only rather than hidden. Someone deciding whether a
 * job needs a site rate wants to see its state and its compliance flags in the
 * same glance, and hiding them would mean opening Sage to answer a question
 * this screen already knows the answer to.
 */
export function JobsPage() {
  const [jobs, setJobs] = useState<JobDetail[]>([])
  const [locations, setLocations] = useState<string[]>([])
  const [search, setSearch] = useState('')
  const [issuesOnly, setIssuesOnly] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [audit, setAudit] = useState<Record<string, JobWriteAudit[]>>({})

  const load = useCallback(async () => {
    setError(null)
    try {
      const [j, l] = await Promise.all([
        api.officeJobs({ search: search.trim() || undefined, issues_only: issuesOnly }),
        api.perDiemLocations(),
      ])
      setJobs(j)
      setLocations(l)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load jobs')
    }
  }, [search, issuesOnly])

  useEffect(() => {
    const t = setTimeout(() => void load(), 250)
    return () => clearTimeout(t)
  }, [load])

  const problems = useMemo(() => jobs.filter((j) => j.issue_severity), [jobs])

  async function setLocation(job: JobDetail, value: string) {
    setBusy(job.job_no)
    setError(null)
    setNotice(null)
    try {
      const updated = await api.updateJob(job.job_no, { per_diem_location: value })
      setJobs((prev) => prev.map((j) => (j.job_no === job.job_no ? updated : j)))

      // Read the write log back rather than trusting the round trip. A write
      // that Sage discarded — because someone had the job open — looks exactly
      // like a successful one from here.
      const writes = await api.jobWrites(job.job_no)
      setAudit((prev) => ({ ...prev, [job.job_no]: writes }))
      const last = writes[0]
      if (last && !last.succeeded) {
        setError(
          `${job.job_no}: Sage did not keep the change — it still reads ` +
            `"${last.verified_value ?? '(blank)'}". Someone probably has the ` +
            'job open in Job Maintenance. Close it there and try again.',
        )
      } else {
        setNotice(
          `${job.job_no} per diem location is now ${value || '(the state rate)'}.`,
        )
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="stack">
      {error && <div className="alert alert-error">{error}</div>}
      {notice && <div className="alert alert-info">{notice}</div>}

      <div className="card">
        <h2 className="title" style={{ marginTop: 0 }}>
          Jobs
        </h2>
        <p className="muted">
          Per diem location writes straight back to Sage
          (<span className="mono">JC_Job.UDF_PERDIEMLOC</span>). Leave it blank
          and the job takes its state's rate — only name a location when the
          state rate is wrong for that site.
        </p>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search job number, description or city"
        />
        <label className="checkline" style={{ marginTop: '0.5rem' }}>
          <input
            type="checkbox"
            checked={issuesOnly}
            onChange={(e) => setIssuesOnly(e.target.checked)}
          />
          <span>
            Only jobs with a per diem problem
            {problems.length > 0 && !issuesOnly && (
              <span className="muted"> — {problems.length} found</span>
            )}
          </span>
        </label>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Job</th>
                <th>Description</th>
                <th>Where</th>
                <th>Flags</th>
                <th>Per diem location</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j.job_no}>
                  <td className="mono">
                    <Link to={`/office/jobs/${j.job_no}`}>{j.job_no}</Link>
                  </td>
                  <td>
                    {j.job_description}
                    {j.issue && (
                      <div
                        className="muted"
                        style={{ fontSize: '0.75rem', marginTop: '0.2rem' }}
                      >
                        {/* 'Check' means a rate resolves but looks wrong —
                            someone overpaid. 'No rate' means nobody is paid. */}
                        <strong>{j.issue_severity}:</strong> {j.issue}
                      </div>
                    )}
                  </td>
                  <td>
                    {j.city}
                    {j.state_code ? `, ${j.state_code}` : ''}
                  </td>
                  <td className="muted" style={{ fontSize: '0.75rem' }}>
                    {[
                      j.is_prevailing_wage && 'Prevailing',
                      j.is_certified && 'Certified',
                      j.is_davis_bacon && 'Davis-Bacon',
                      j.is_union && 'Union',
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </td>
                  <td>
                    <select
                      value={j.per_diem_location ?? ''}
                      disabled={busy === j.job_no}
                      onChange={(e) => void setLocation(j, e.target.value)}
                      aria-label={`Per diem location for ${j.job_no}`}
                    >
                      <option value="">
                        {j.state_code ? `${j.state_code} state rate` : '(no state)'}
                      </option>
                      {/* Driven from the rate table, so the list can never
                          offer a code with no rate behind it. */}
                      {locations.map((l) => (
                        <option key={l} value={l}>
                          {l}
                        </option>
                      ))}
                    </select>
                    {audit[j.job_no]?.[0] && !audit[j.job_no][0].succeeded && (
                      <div className="muted" style={{ fontSize: '0.75rem' }}>
                        last write did not stick
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {jobs.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted">
                    No jobs match.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
